/**
 * LES PISTES DE CONVENTIONS — une ligne par clinique, toujours la même.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── Le défaut (Inès, 05/10/2026) ───────────────────────────────────────────
 *
 * « Le calendrier des conventions, je voudrais qu'il soit toujours dans le même
 *   ordre d'affichage. Chacun a une ligne dans le planning mensuel.
 *   Actuellement lundi je vois vert bleu jaune et mardi vert puis jaune. Il
 *   faut que ce soit dans l'ordre toujours. »
 *
 * La grille empilait les conventions PRÉSENTES chaque jour. Trois le lundi,
 * deux le mardi : la clinique ambre passait de la 3e à la 2e ligne. Une couleur
 * qui change de place d'un jour au suivant n'est plus un repère, c'est une
 * devinette — on ne peut plus suivre une clinique du regard le long d'une ligne.
 *
 * ── La correction : des PISTES, pas une pile ───────────────────────────────
 *
 * On calcule une fois, pour la période affichée, la liste ordonnée des
 * conventions qui y apparaissent. Chaque jour réserve ensuite une ligne par
 * piste, colorée si la convention tombe ce jour-là, VIDE sinon.
 *
 * ⚠️ SUR LA PÉRIODE AFFICHÉE, et non sur toutes les conventions existantes :
 * réserver une piste à une clinique qui ne passe pas du tout ce mois-ci
 * ajouterait une ligne vide à chaque case, pour rien. Conséquence assumée :
 * changer de mois peut changer le NOMBRE de pistes — mais à l'intérieur d'un
 * mois, l'ordre ne bouge plus, et c'est ce qui était demandé.
 *
 * ── Module PUR, et pourquoi il est séparé de `conventions.ts` ──────────────
 *
 * `conventions.ts` porte `'use client'` et importe React (il expose un hook) :
 * le lanceur de tests ne sait pas le compiler. Cette règle-ci est donc dans une
 * FEUILLE, sans aucun import d'exécution, et éprouvée par
 * `backend/tests/front-conventions-pistes.test.js`. Même raison qui a fait
 * sortir `CleSelection` de `store.ts`.
 */

/** Ce qu'on lit d'une occurrence. Forme STRUCTURELLE : le moteur en pose
 *  davantage (nature, semaine, bornes de période), on n'en lit que ceci. */
export interface OccurrencePiste {
  date: string;
  conventionId: string;
  nom?: string;
  couleur?: string | null;
}

/** Une convention telle que la grille lui réserve une ligne. */
export interface PisteConvention {
  conventionId: string;
  nom: string;
  couleur: string | null;
}

/**
 * Les conventions de la période, DANS L'ORDRE, une fois chacune.
 *
 * ⚠️ ON NE RETRIE PAS. Les occurrences arrivent déjà triées par le moteur
 * (`conventions-calendrier.ts` : date, puis `ordre` de la fiche, puis
 * l'alphabet). Un second tri ici aurait fini par dire autre chose que le
 * calendrier et le paramétrage — c'est l'ordre d'apparition qui fait foi.
 */
export function pistesConventions(occ: OccurrencePiste[]): PisteConvention[] {
  const vues = new Map<string, PisteConvention>();
  for (const o of occ || []) {
    const id = String(o?.conventionId || '');
    if (!id || vues.has(id)) continue;
    vues.set(id, { conventionId: id, nom: String(o.nom || ''), couleur: o.couleur ?? null });
  }
  return [...vues.values()];
}

/**
 * Les cases d'UN jour : une par piste, dans l'ordre des pistes.
 *
 * `presente` dit s'il faut colorer. On rend TOUJOURS autant d'entrées qu'il y a
 * de pistes — c'est ce qui garantit la ligne stable, et c'est pour ça que la
 * fonction ne filtre pas : un tableau raccourci reproduirait le défaut.
 */
export function casesDuJour(
  pistes: PisteConvention[],
  duJour: OccurrencePiste[] | undefined,
): { piste: PisteConvention; presente: boolean }[] {
  const ids = new Set((duJour || []).map((o) => String(o?.conventionId || '')));
  return (pistes || []).map((piste) => ({ piste, presente: ids.has(piste.conventionId) }));
}

/** Y a-t-il quelque chose à dessiner ce jour-là ? Évite de poser une colonne de
 *  lignes vides sur un jour sans aucune convention. */
export const jourAvecConvention = (duJour: OccurrencePiste[] | undefined): boolean => !!duJour?.length;
